import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/redux/Store';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSync, FaPlus, FaCheck, FaTimes, FaEdit } from 'react-icons/fa';
import api from '@/utils/api';
import { formatRelativeTime } from '@/utils/formatTime';
import './ChatRoomsAdmin.scss';

// Suggested starter rooms — mirrors
// server/migrations/pg/1781400000000_seed_starter_chat_rooms.js so a dev
// who skipped the migration can still pick them up. If you change
// either side, update the other so the migration and the picker stay
// aligned.
interface SuggestedRoom {
  slug: string;
  name: string;
  description: string;
  sortOrder: number;
}
const SUGGESTED_ROOMS: SuggestedRoom[] = [
  {
    slug: 'trading',
    name: 'Trading',
    description: 'Profile prices, royalties, marketplace, pool, ITO — anything money-flow.',
    sortOrder: 1,
  },
  {
    slug: 'announcements',
    name: 'Announcements',
    description: 'Platform updates, downtime, new features. Read-only is on the roadmap.',
    sortOrder: 2,
  },
  {
    slug: 'help',
    name: 'Help',
    description: 'Onboarding questions, wallet setup, paywalled posts — anything support-adjacent.',
    sortOrder: 3,
  },
  {
    slug: 'lounge',
    name: 'Lounge',
    description: 'Off-topic. Coffee, music, whatever — not Klyx-related.',
    sortOrder: 4,
  },
];

interface ChatRoom {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  type: string;
  sort_order: number;
  is_active: boolean;
  created_at?: string;
}

interface EditDraft {
  name: string;
  description: string;
  sortOrder: number;
}

interface CreateDraft {
  slug: string;
  name: string;
  description: string;
  sortOrder: number;
}

const emptyCreateDraft: CreateDraft = { slug: '', name: '', description: '', sortOrder: 0 };

const ChatRoomsAdmin: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);

  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createDraft, setCreateDraft] = useState<CreateDraft>(emptyCreateDraft);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [addingSlug, setAddingSlug] = useState<string | null>(null);

  // Slug → existing room from the API. Used by the picker so we can
  // show "Added" instead of "+ Add" for suggestions that are already in
  // the DB (either from the migration or a previous click here).
  const existingBySlug = useMemo(() => {
    const map = new Map<string, ChatRoom>();
    rooms.forEach((r) => map.set(r.slug, r));
    return map;
  }, [rooms]);
  const missingSuggestions = SUGGESTED_ROOMS.filter((s) => !existingBySlug.has(s.slug));

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'moderator')) {
      navigate('/');
      return;
    }
    fetchRooms();
  }, [user, navigate]);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{ rooms: ChatRoom[] }>('/chat/rooms/admin');
      setRooms(data.rooms);
    } catch (err: any) {
      console.error('Failed to load chat rooms:', err);
      toast.error(err.response?.data?.error || 'Failed to load chat rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const slug = createDraft.slug.trim().toLowerCase();
    const name = createDraft.name.trim();
    if (!slug || !name) {
      toast.error('Slug and name are required');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(slug)) {
      toast.error('Slug must be lowercase letters, numbers, and hyphens only');
      return;
    }
    setCreating(true);
    try {
      await api.post('/chat/rooms', {
        slug,
        name,
        description: createDraft.description.trim() || null,
        sortOrder: createDraft.sortOrder,
      });
      toast.success(`Created #${slug}`);
      setCreateDraft(emptyCreateDraft);
      setShowCreate(false);
      fetchRooms();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to create room');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (room: ChatRoom) => {
    setEditingId(room.id);
    setEditDraft({
      name: room.name,
      description: room.description ?? '',
      sortOrder: room.sort_order,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const saveEdit = async (roomId: string) => {
    if (!editDraft) return;
    setSavingId(roomId);
    try {
      await api.patch(`/chat/rooms/${roomId}`, {
        name: editDraft.name.trim(),
        description: editDraft.description.trim() || null,
        sortOrder: editDraft.sortOrder,
      });
      toast.success('Room updated');
      cancelEdit();
      fetchRooms();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to update room');
    } finally {
      setSavingId(null);
    }
  };

  const addSuggested = async (suggestion: SuggestedRoom) => {
    setAddingSlug(suggestion.slug);
    try {
      await api.post('/chat/rooms', {
        slug: suggestion.slug,
        name: suggestion.name,
        description: suggestion.description,
        sortOrder: suggestion.sortOrder,
      });
      toast.success(`Added #${suggestion.slug}`);
      fetchRooms();
    } catch (err: any) {
      // 409 means another admin / this admin in another tab just added it.
      // Either way the picker will reflect the truth on the next fetch.
      if (err.response?.status === 409) {
        toast.info(`#${suggestion.slug} already exists`);
        fetchRooms();
      } else {
        toast.error(err.response?.data?.error || 'Failed to add room');
      }
    } finally {
      setAddingSlug(null);
    }
  };

  const toggleActive = async (room: ChatRoom) => {
    setSavingId(room.id);
    try {
      await api.patch(`/chat/rooms/${room.id}`, { isActive: !room.is_active });
      toast.success(room.is_active ? `#${room.slug} deactivated` : `#${room.slug} activated`);
      fetchRooms();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to toggle');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="chat-rooms-admin-page">
      <div className="page-header">
        <div>
          <h1>Chat Rooms</h1>
          <p>Create and manage the public chat rooms shown to all users.</p>
        </div>
        <div className="header-actions">
          <button className="btn-refresh" onClick={fetchRooms} disabled={loading} title="Refresh">
            <FaSync className={loading ? 'spinning' : ''} />
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowCreate((v) => !v)}
            aria-expanded={showCreate}
          >
            <FaPlus /> New room
          </button>
        </div>
      </div>

      {/* Quick-add picker. Each suggestion mirrors the seed migration
          (1781400000000) — clicking adds the room with the migration's
          defaults so dev environments that skipped the migration can
          curate the same set. Existing slugs are filtered out so the
          list shrinks to zero as you fill in the gaps. */}
      {!loading && missingSuggestions.length > 0 && (
        <div className="suggested-card">
          <div className="suggested-card__head">
            <h2>Suggested rooms</h2>
            <p>Quick-add rooms with sensible defaults. You can edit or deactivate them later.</p>
          </div>
          <div className="suggested-grid">
            {missingSuggestions.map((s) => (
              <button
                key={s.slug}
                type="button"
                className="suggested-chip"
                onClick={() => addSuggested(s)}
                disabled={addingSlug === s.slug}
                title={s.description}
              >
                <span className="suggested-chip__slug">#{s.slug}</span>
                <span className="suggested-chip__name">{s.name}</span>
                <span className="suggested-chip__action">
                  {addingSlug === s.slug ? 'Adding…' : <><FaPlus /> Add</>}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {showCreate && (
        <div className="create-card">
          <h2>Create a new room</h2>
          <div className="create-grid">
            <label>
              <span>Slug</span>
              <input
                type="text"
                placeholder="trading"
                value={createDraft.slug}
                onChange={(e) => setCreateDraft({ ...createDraft, slug: e.target.value })}
                disabled={creating}
              />
              <small>Lowercase letters, numbers, and hyphens only. Becomes part of the URL.</small>
            </label>
            <label>
              <span>Name</span>
              <input
                type="text"
                placeholder="Trading"
                value={createDraft.name}
                onChange={(e) => setCreateDraft({ ...createDraft, name: e.target.value })}
                disabled={creating}
              />
            </label>
            <label className="span-2">
              <span>Description</span>
              <input
                type="text"
                placeholder="What's this room for?"
                value={createDraft.description}
                onChange={(e) => setCreateDraft({ ...createDraft, description: e.target.value })}
                disabled={creating}
              />
            </label>
            <label>
              <span>Sort order</span>
              <input
                type="number"
                value={createDraft.sortOrder}
                onChange={(e) =>
                  setCreateDraft({ ...createDraft, sortOrder: parseInt(e.target.value || '0', 10) })
                }
                disabled={creating}
              />
              <small>Lower = higher in the list. Global is 0; defaults to 0.</small>
            </label>
          </div>
          <div className="create-actions">
            <button className="btn-secondary" onClick={() => setShowCreate(false)} disabled={creating}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating…' : 'Create room'}
            </button>
          </div>
        </div>
      )}

      <div className="rooms-table-wrap">
        {loading && rooms.length === 0 ? (
          <div className="loading">Loading rooms…</div>
        ) : (
          <table className="rooms-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Slug</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((room) => {
                const isEditing = editingId === room.id;
                const isSaving = savingId === room.id;
                return (
                  <tr key={room.id} className={room.is_active ? '' : 'inactive'}>
                    <td className="num-cell">
                      {isEditing && editDraft ? (
                        <input
                          type="number"
                          value={editDraft.sortOrder}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, sortOrder: parseInt(e.target.value || '0', 10) })
                          }
                          disabled={isSaving}
                        />
                      ) : (
                        room.sort_order
                      )}
                    </td>
                    <td className="slug-cell">#{room.slug}</td>
                    <td>
                      {isEditing && editDraft ? (
                        <input
                          type="text"
                          value={editDraft.name}
                          onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                          disabled={isSaving}
                        />
                      ) : (
                        room.name
                      )}
                    </td>
                    <td>
                      {isEditing && editDraft ? (
                        <input
                          type="text"
                          value={editDraft.description}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, description: e.target.value })
                          }
                          disabled={isSaving}
                        />
                      ) : (
                        <span className="desc">{room.description || '—'}</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`status-pill ${room.is_active ? 'active' : 'inactive'}`}
                        onClick={() => toggleActive(room)}
                        disabled={isSaving}
                        title={room.is_active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {room.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="muted">
                      {room.created_at ? formatRelativeTime(room.created_at) : '—'}
                    </td>
                    <td className="action-cell">
                      {isEditing ? (
                        <>
                          <button
                            className="btn-icon save"
                            onClick={() => saveEdit(room.id)}
                            disabled={isSaving}
                            title="Save"
                          >
                            <FaCheck />
                          </button>
                          <button
                            className="btn-icon cancel"
                            onClick={cancelEdit}
                            disabled={isSaving}
                            title="Cancel"
                          >
                            <FaTimes />
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn-icon edit"
                          onClick={() => startEdit(room)}
                          disabled={isSaving}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rooms.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty">
                    No rooms yet. Click <strong>New room</strong> to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ChatRoomsAdmin;
